<template lang="pug">
.conversation-log(:class="{ 'is-sms-channel': isSMSChannel }")
  .created-at-header
    .header-line
    span {{createdAt}}
    .header-line
  .msg-container
    template(v-for="message in processedMessages")
      aiq-tooltip(v-if="showDeletedMessage(message)",
                  effect="dark",
                  placement="top-end",
                  :show-after="1000",
                  :content="tooltipMessage(message)",
                  :disabled="!message.deleted_at" )
        message(:class="{ deleted: !!message.deleted_at }"
                :key="message.id + message.obfuscated"
                :message="message"
                :systemMessagesDisplay="systemMessagesDisplay"
                :disabled="message.disabled"
                :replyitem="message.replyitem"
                :showIcons="getIconVisibilityConfig(message)"
                :callJoined="callJoined"
                :isTimestampAbsolute="isTimestampAbsolute"
                :accountFieldsVisibility="accountFields"
                :connected="customer.connected"
                :showOnlineIndicator="true"
                :showTranscript="featureFlags.SHOW_CALL_TRANSCRIPT"
                @obfuscate="onObfuscate"
                @call="$emit('call', $event)"
                @suggestionsClick="selectSuggestions"
                @click="onClickMessage"
                @translate="onTranslate"
                @delete="setMessageToDelete"
                @link="onLink"
                @screenshare="onScreenShare"
                @focusOnInputbox="focusOnInputbox")
  .conversation-actions

    template(v-if="isConversationSummarizable(conversationId)")
      .summary-box(v-if="summary")
        font-awesome-icon(icon="sparkle")
        span {{summary}}
      aiq-button(v-else @click="onSummary(conversationId)")
        template(v-if="isSummarizing")
          font-awesome-icon(icon="spinner" spin)
        template(v-else)
          font-awesome-icon(icon="sparkles")
          span {{ $t('conversation_tab.chat_panel.summarize') }}
    aiq-button(v-if="canReview" @click="openReviewDialog({ conversation, agentsWhoContributed })")
      font-awesome-icon(v-if="isReviewedByMe" :icon="['fas', 'check']")
      font-awesome-icon(v-else icon="plus")
      span Review this conversation
  .conversation__info
    .tags
      aiq-tooltip(v-for="tag in conversationTags"
                  :key="tag.id"
                  effect="dark"
                  content="Tag"
                  placement="top"
                  :show-after="1000")
        aiq-tag {{tag.name}}
    .categories
      aiq-tooltip(v-for="category in conversationCategories"
                  :key="category.id"
                  effect="dark"
                  content="Category"
                  placement="top"
                  :show-after="1000")
        aiq-tag {{category.name}}
    .teams
      aiq-tooltip(v-for="team in conversationTeams"
                  :key="team.id"
                  effect="dark"
                  content="Team"
                  placement="top"
                  :show-after="1000")
        aiq-tag {{team.name}}

  delete-message-confirm(:message="messageToDelete"
                        @cancel="setMessageToDelete"
                        @confirm="onDelete")
</template>

<script>
import get from 'lodash/get';
import has from 'lodash/has';
import set from 'lodash/set';
import assign from 'lodash/assign';
import isEmpty from 'lodash/isEmpty';
import { AGENT_PROFILE_MAPPING, CHANNELS } from '@/constants';
import { mapGetters, mapActions, mapState } from 'vuex';
import DeleteMessageConfirm from './components/DeleteMessageConfirm';
import dayjs from '@/localPlugins/dayjs';
import LanguageDetect from 'languagedetect';
import * as log from 'loglevel';
import SummaryMixin from '@/libs/mixins/Summary';

const MIN_ACCEPTABLE_PROBABILITY = 0.22;
const TAG = 'ConversationLog';

export default {
  name: 'conversationLog',
  components: {
    DeleteMessageConfirm,
  },
  mixins: [SummaryMixin],
  props: {
    messages: {
      type: Array,
      required: true,
    },
    customer: {
      type: Object,
      required: true,
    },
    callJoined: {
      type: Boolean,
    },
    conversationId: {
      type: String,
      required: true,
    },
    conversation: {
      type: Object,
      required: true,
    },
  },
  inject: ['focusOnInputbox'],
  data() {
    return {
      lngDetector: null,
      messageToDelete: null,
      selectedReview: null,
      agentsWhoContributed: [],
    };
  },
  created() {
    this.lngDetector = new LanguageDetect();
    this.lngDetector.setLanguageType('iso2');

    const promiseList = [
      this.$store.dispatch('configs/getTransferChatConfigs'),
      this.$store.dispatch('configs/getWebchatLanguageSettings'),
    ];
    Promise.all(promiseList).then(() => {
      this.getWebchatLanguageStrings(get(this.webchatLanguageSettings, 'selected_language_view', 'en'));
    }).catch((error) => {
      log.error(`${TAG}: failed to load transfer chat configuration or webchat language settings`, error);
      this.getWebchatLanguageStrings('en');
    });
  },
  mounted() {
    this.agentsWhoContributed = this.getAgentsWhoContributed();
  },
  computed: {
    ...mapGetters({
      showSuggestIcons: 'conversations/existingItemSuggestions',
      bot: 'conversations/bot',
    }),
    ...mapState({
      agentProfile: state => state.agent.profile,
      systemMessagesDisplay: state => state.conversations.systemMessagesDisplay,
      translationRecords: state => state.translation.records,
      COBROWSE_AUDIT_VIEW: state => state.featureFlags.FEATURE_FLAGS.COBROWSE_AUDIT_VIEW,
      isTimestampAbsolute: state => state.conversations.isTimestampAbsolute,
      accountFields: state => state.configs.config.account_fields,
      featureFlags: state => state.featureFlags.FEATURE_FLAGS,
      conversationSummaryConfig: state => state.featureFlags.conversationSummaryConfig,
      reviewsList: state => state.conversationReviews.reviewsList,
      isReviewsListLoading: state => state.conversationReviews.isListLoading,
      transferChatConfigs: state => state.configs.channels.webchat.transfer_chat_configs,
      webchatLanguageStrings: state => state.configs.language_strings,
      webchatLanguageSettings: state => state.configs.language_setting,
    }),
    createdAt() {
      const time = get(this.conversation, 'created_at');
      if (!time) {
        return '';
      }
      const useCalendar = dayjs().isSame(dayjs(time), 'day') ||
        dayjs().isSame(dayjs(time).subtract(1, 'days'));

      return useCalendar
        ? dayjs(time).calendar()
        : dayjs(time).format('lll');
    },
    conversationTags() {
      return get(this.conversation, 'tags', []);
    },
    conversationCategories() {
      return get(this.conversation, 'categories', []);
    },
    conversationTeams() {
      return get(this.conversation, 'teams', []);
    },
    isSMSChannel() {
      return get(this.conversation, 'channel') === CHANNELS.SMS;
    },
    canReview() {
      return !this.isReviewsListLoading && this.canView('/conversations/review_conversations') && !isEmpty(this.agentsWhoContributed);
    },
    isReviewedByMe() {
      return this.reviewsList.some(review => review.conversation_id === Number(this.conversationId) && review.reviewer.id === this.agentProfile.id);
    },
    isTransferChatSummaryEnabled() {
      return get(this.transferChatConfigs, 'summary.enabled', false);
    },
    processedMessages() {
      if (!this.isTransferChatSummaryEnabled) {
        return this.messages.filter((message) => get(message, 'payload.message_type', '') !== 'summary');
      }
      return this.messages;
    },
  },
  watch: {
    messages() {
      // The messages array change from the parent layers multiple times for each message sent without changing its length
      // so, we need to debounce it to reduce the number of times we check for agents
      setTimeout(() => {
        this.agentsWhoContributed = this.getAgentsWhoContributed();
      }, 1000); // eslint-disable-line
    },
  },
  methods: {
    ...mapActions({
      selectSuggestions: 'conversations/selectSuggestions',
      obfuscateMessage: 'conversations/obfuscateMessage',
      deleteMessage: 'conversations/deleteMessage',
      openReviewDialog: 'conversationReviews/openDialog',
    }),
    async getWebchatLanguageStrings(languagekey) {
      try {
        await this.$store.dispatch('configs/getWebchatLanguageStrings', { languagekey });
      } catch (err) {
        log.error('Unable to load the language strings.', err);
        this.$aiq.notify.error('Unable to load the language strings.');
      }
    },
    getAgentVariableMap() {
      const resultMap = new Map();
      for (const variable of AGENT_PROFILE_MAPPING) {
        const value = get(this.agentProfile, variable.field, '');
        if (variable.field) {
          resultMap.set(variable.field, value);
        }
        if (variable.label) {
          resultMap.set(variable.label, value);
        }
      }
      return resultMap;
    },
    getAgentsWhoContributed() {
      const agentsWhoContributed = new Map();
      for (const { sender_type, bot, event, sender } of this.messages) {
        if (sender_type === 'agents' && bot === false && event === false && has(sender, 'id')) {
          if (!agentsWhoContributed.has(sender.id)) {
            agentsWhoContributed.set(sender.id, sender);
          }
        }
      }
      return Array.from(agentsWhoContributed.values());
    },
    getIconVisibilityConfig(message) {
      // TODO: feature flag
      return assign({
        documents: false,
        assets: false,
        obfuscation: this.canView('/conversations/message/obfuscation'),
        translation: this.canTranslate(message),
        delete: this.canDelete(message),
      }, this.showSuggestIcons[message.id]);
    },
    showDeletedMessage(message) {
      return !message.deleted_at || this.canView('/conversations/message/audit');
    },
    onScreenShare(msg) {
      const url = get(msg, 'payload.call.content');
      window.open(url, '_blank');
    },
    tooltipMessage({ deleted_by, deleted_by_agent, payload }) {
      const { expiration_date } = payload;
      const botId = get(this.$store.getters['conversations/bot'], 'id');
      if (expiration_date && deleted_by === botId) return `Expired ${dayjs(expiration_date).format('YYYY-MM-DD')}`;
      return `Deleted by ${get(deleted_by_agent, 'fullName', '')}`;
    },
    onObfuscate(message) {
      return this.$aiq.confirm(
        'Message Masking Confirmation',
        'Message masking is permanent. <br> The message will be replaced with asterisks ( * ) <br> Do you want to proceed?',
        { confirmButtonText: 'Proceed' },
      )
        .then(confirm => {
          if (confirm) {
            this.obfuscateMessage(message)
              .then(() => this.$aiq.notify.success('Successful'))
              .catch(() => this.$aiq.notify.error('Unable to mask the message'));
          }
        }, _cancel => {
        });
    },
    canTranslate(message) {
      if (!this.canView('/conversations/message/translation') || message.deleted_at) {
        return false;
      }
      const message_type = get(message, 'payload.message_type', '');
      const contentPath = message_type ? `payload.${message_type}.content` : 'payload.content';
      const content = get(message, contentPath, '');
      const obfuscated = get(message, 'obfuscated', false);
      const translated = get(message, 'translated', false);
      const detections = this.lngDetector.detect(content);
      const detection = detections.find(([x]) => x === 'en');

      // Determine that the content needs to be translated based on
      // the probability of 20% or less it's in english
      const canTranslate = (detection && (detection[1] < MIN_ACCEPTABLE_PROBABILITY));
      // We show icon if message have content and is not obfuscated and
      // was not possible determine the language or
      // the content was already translated or
      // the probability of 20% or less it's in english
      const show = content && !obfuscated && (!detection || translated || canTranslate);
      return {
        show,
        icon: translated ? 'arrow-rotate-left' : 'language',
      };
    },
    canDelete({ sender_id, sender_type, deleted_at }) {
      const botId = get(this.bot, 'id');
      if (deleted_at || sender_type !== 'agents' || sender_id === botId) {
        return false;
      }
      // "My Message" and "Another Agent's Message" are independent ACL permissions (AG-23661).
      const isOwnMessage = sender_id === get(this.agentProfile, 'id');
      return isOwnMessage
        ? this.canView('/conversations/message/delete')
        : this.canView('/conversations/message/delete_any');
    },
    trySaveCustomerLanguageCode(languageCode, sender_type) {
      const currentLanguageCode = get(this.customer, 'profile.language_code', '');
      if (languageCode === currentLanguageCode || sender_type !== 'customers') {
        return;
      }
      set(this.customer, 'profile.language_code', languageCode);
      this.$store.dispatch('conversations/updateCustomer', [
        this.customer.id,
        { ...this.customer },
      ]);
    },
    setMessageToDelete(message = null) {
      this.messageToDelete = message;
    },
    async onTranslate(message) {
      try {
        const id = get(message, 'id', 0);
        const message_type = get(message, 'payload.message_type', '');
        const path = message_type ? `payload.${message_type}` : 'payload';
        const content = get(message, `${path}.content`, '');
        const originalContent = get(message, `${path}.originalContent`, '');
        const translated = get(message, 'translated', false);
        if (translated) {
          const record = this.translationRecords.find(t => t.id === id);
          set(message, `${path}.content`, record.q);
          message['translated'] = false;
          delete get(message, path).translation;
          return;
        }
        const { translation, language } = await this.$store.dispatch('translation/translate', { id, q: content, target: 'en' });
        this.trySaveCustomerLanguageCode(get(language, 'code', ''), message.sender_type);
        if (get(message, `${path}.content`, '') === translation) {
          this.$aiq.notify.info('Translated to the same content');
        }
        set(message, `${path}.content`, originalContent || translation);
        message['translated'] = true;
        get(message, path)['translation'] = language ? { from: language.code, to: 'en' } : null;
      } catch (err) {
        this.$aiq.notify.error('Unable to translate content');
      }
    },
    async onDelete(message) {
      try {
        this.setMessageToDelete();
        const payload = { conversationId: message.conversation_id, messageId: message.id };
        await this.deleteMessage(payload);
        this.$aiq.notify.success('Successful');
      } catch (err) {
        log.error(err);
        this.$aiq.notify.error('Unable to delete the message');
      }
    },
    async onClickMessage(msg) {
      // POC && Cobrowse audit view
      if (this.COBROWSE_AUDIT_VIEW && get(msg, 'payload.system.content', '').startsWith('Cobrowse Session: ')) {
        const str = get(msg, 'payload.system.content', '');
        const match = str.match(/Cobrowse Session: ([a-zA-Z0-9]+) by ([a-zA-Z0-9@.]+)$/i);
        const token = match[1];
        const email = match[2];

        const url = await this.$store.dispatch('agent/getCobrowseAuditUrl', {
          identity: email,
          session: token,
        });

        log.debug(TAG, 'url', url);

        window.open(url, '_blank', 'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no');
      }
    },
    onLink({ url /* type */ }) { // type can be hyperlink or document at this moment.
      window.open(url, '_blank'); // open anything with new tab
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../../styles/aiq-mixins.scss";
@import "../../../../../../styles/media-queries.scss";

.created-at-header {
  display: flex;
  height: 14px;
  margin-top: 10px;
  margin-bottom: 20px;

  @include mobile {
    margin-bottom: 7px;
  }

  .header-line {
    border-bottom: 1px solid #eee;
    flex-grow: 1;
    height: 10px;
  }

  span {
    color: #45494f;
    font-size: 14px;
    font-family: -apple-system,BlinkMacSystemFont,Helvetica,Arial,sans-serif;
    margin: 0 16px;
    opacity: 0.75;
  }
}

.conversation-actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 5px;

  & > * {
    background: #BCF5E4;
    box-shadow: 4px 1px 15px #d3d3d3;
    border: none;
    color: #41474d;
    margin-top: 5px;
    margin-bottom: 5px;

    .fa-sparkle, .fa-sparkles, .fa-plus, .fa-check {
      margin-right: 5px;
    }

    .fa-sparkle, .fa-sparkles, .fa-plus {
      color: #FFD43B;
    }

    .fa-check {
      color: green;
    }
  }

  & > button {
    border-radius: 50px;
  }

  .summary-box {
    text-align: center;
    border-radius: 5px;
    padding: 5px 50px;
    text-align: center;
  }
}

.conversation__info {
  display: flex;
  @include space-between-h(4px);

  > div {
    @include space-between-h(4px);

    cursor: default;
  }
}

.conversation-log :deep(.attachment-file-name-tooltip) {
  &.tooltip-container {
    .tooltip-box {
      line-height: 1.1rem;
    }
  }
}

</style>

<style lang="scss">
@import "../../../../../../styles/media-queries.scss";
.tags {
  .el-tag {
    background-color: #fbeefd;
    color: #dd55f2;
  }
  }
.categories {
  .el-tag {
    background-color: #fff8ed;
    color: #f29900;
  }
}
.teams {
  .el-tag {
    background-color: #e3ecff;
    color: #5a88de;
  }
}
.danger-button {
  background-color: #ff4949 !important;
  border-color: #ff4949 !important;
}
.message-list_item.deleted {
  opacity: .5;
}

.message-bubble_content h1, .message-bubble_content h2, .message-bubble_content h3, .message-bubble_content h4, .message-bubble_content h5, .message-bubble_content h6, .message-bubble_content p, .message-bubble_content li, .message-bubble_content span, .message-bubble_content div {
  color: white !important;
}

.conversation-log {
  a[aiq-link] * {
    pointer-events: none;
  }

  a[aiq-link] {
    pointer-events: auto;
    color: var(--el-color-primary-light-5);
  }

  .message-item {
    container-type: inline-size;
  }

  .message-item > .el-dropdown {
    width: fit-content;
  }

  .message-bubble .message-attachments {
    width: 100%;
    min-width: 12rem;
    max-width: 85%;
    max-width: 85cqw;

    .message-attachment {
      .attachment-card__name {
        font-size: 12px;

        .file-name-only {
          max-width: none;
        }
      }
    }
  }

  /* SMS plain text uses \n for breaks; */
  &.is-sms-channel {
    .message-bubble_content {
      white-space: pre-line;
    }
  }
}

</style>
